// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateLoadParameter from "@insite/client-framework/Store/Pages/NewsList/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainer, GridItem } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button, { ButtonPresentationProps } from "@tps/MobiusOverrides/Button/Button";
import LazyImage, { LazyImageProps } from "@tps/MobiusOverrides/LazyImage/LazyImage";
import parse from "html-react-parser";
import sortBy from "lodash/sortBy";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    defaultPageSize = "defaultPageSize",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.defaultPageSize]: number;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: WidgetProps) => {
    const pages = getDataView(state.data.pages, { parentNodeId: getCurrentPage(state).nodeId });
    let links: SafeDictionary<PageLinkModel> = {};
    if (pages.value) {
        links = pages.value.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
    }
    return {
        parentNodeId: getCurrentPage(state).nodeId,
        location: getLocation(state),
        pages,
        links,
        parameter: state.pages.newsList.getNewsPagesParameters[getCurrentPage(state).nodeId] ?? {
            parentNodeId: getCurrentPage(state).nodeId,
        },
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
    updateLoadParameter,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory & HasShellContext;

export interface NewsListViewStyles {
    titleText?: TypographyProps;
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    listWrapper?: InjectableCss;
    newsWrapper?: InjectableCss;
    summaryWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    readMoreLink?: LinkPresentationProps;
    pagination?: PaginationPresentationProps;
    // Xng-Changes:
    listGridContainer?: GridContainerProps;
    newsGridItem?: GridItemProps;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    titleLink?: LinkPresentationProps;
    summaryText?: TypographyProps;
    seperator?: TypographyProps;
    readMoreButton?: ButtonPresentationProps;
    buttonContainer?: InjectableCss;
    paginationGridItem?: GridItemProps;
}

export const newsListViewStyles: NewsListViewStyles = {
    titleText: {
        size: 20,
        css: css`
            margin-bottom: 0;
        `,
    },
    newsWrapper: {
        css: css`
            margin-bottom: 15px;
            border-top: 1px solid ${getColor("common.border")};
            padding-top: 15px;
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                border-top: 1px solid ${getColor("common.border")};
                padding-top: 15px;
            `,
        },
    },
    summaryWrapper: {
        css: css`
            margin-top: 8px;
        `,
    },
    authorText: {
        css: css`
            font-weight: bold;
            margin-right: 8px;
        `,
    },
    publishDateText: {
        css: css`
            font-style: italic;
        `,
    },
};

const styles = newsListViewStyles;

class NewsListView extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        if (!this.props.pages.value && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        this.checkQueryString();
    }

    componentDidUpdate() {
        if (!this.props.pages.value && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        this.checkQueryString();
    }

    changePage = (newPageIndex: number) => {
        this.updateQueryString(newPageIndex, this.props.parameter.pageSize);
    };

    changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        this.updateQueryString(1, newPageSize);
    };

    updateQueryString(page: number, pageSize?: number) {
        const {
            history,
            location: { pathname },
        } = this.props;
        let newLocation = `${pathname}?page=${page}`;
        if (pageSize) {
            newLocation += `&pageSize=${pageSize}`;
        }
        history.push(newLocation);
    }

    checkQueryString() {
        const {
            location: { search },
            parameter: { page, pageSize, parentNodeId },
        } = this.props;
        const parsedQueryString = parseQueryString<{ page?: string; pageSize?: string }>(search);
        const newPage = parsedQueryString.page ? convertToNumberIfString(parsedQueryString.page) : undefined;
        const newPageSize = parsedQueryString.pageSize
            ? convertToNumberIfString(parsedQueryString.pageSize)
            : undefined;
        if (newPage !== page || newPageSize !== pageSize) {
            this.props.updateLoadParameter({
                page: newPage,
                pageSize: newPageSize,
                parentNodeId,
            });
        }
    }

    handleButtonOnClick(linkUrl: string | undefined) {
        if (linkUrl) {
            this.props.history.push(linkUrl);
        }
    }

    render() {
        const {
            links,
            pages: { value: pages },
            fields: { defaultPageSize },
            parameter,
        } = this.props;

        if (!pages) {
            return null;
        }

        const totalItems = pages.length;
        const page = parameter.page ?? 1;
        const pageSize = parameter.pageSize ?? defaultPageSize;

        const sortedAndPagedPages = sortBy(pages, o => o.fields["publishDate"])
            .reverse()
            .slice((page - 1) * pageSize, page * pageSize);

        // Xng-Changes: structure and styles
        return (
            <GridContainer {...styles.listGridContainer}>
                {sortedAndPagedPages.map((page: PageProps) => (
                    <GridItem {...styles.newsGridItem} key={page.id}>
                        <StyledWrapper {...styles.newsWrapper}>
                            {page.fields["image"] && (
                                <StyledWrapper {...styles.imageWrapper}>
                                    <LazyImage {...styles.image} src={page.fields["image"]} data-test-selector="newslist_pageimage"></LazyImage>
                                </StyledWrapper>
                            )}
                            <Link {...styles.titleLink} href={links[page.nodeId]?.url} data-test-selector="newslist_pagetitle">
                                {page.fields["title"]}
                            </Link>
                            {(page.fields["newsSummary"] || page.fields["newsContent"]) && (
                                <Typography {...styles.summaryText} data-test-selector="newslist_content">
                                    {parse(
                                        page.fields["newsSummary"] || page.fields["newsContent"],
                                        parserOptions,
                                    )}
                                </Typography>
                            )}
                            <StyledWrapper {...styles.buttonContainer}>
                                <StyledWrapper {...styles.dateAndAuthorWrapper}>
                                    {page.fields["author"] && (
                                        <Typography {...styles.authorText} data-test-selector="newslist_author">
                                            {page.fields["author"]}
                                        </Typography>
                                    )}
                                    {page.fields["author"] && page.fields["publishDate"] && (
                                        <Typography {...styles.seperator} />
                                    )}
                                    {page.fields["publishDate"] && (
                                        <Typography {...styles.publishDateText} data-test-selector="newslist_publishdate">
                                            <LocalizedDateTime
                                                dateTime={new Date(page.fields["publishDate"])}
                                                options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                            />
                                        </Typography>
                                    )}
                                </StyledWrapper>
                                <Button
                                    {...styles.readMoreButton}
                                    data-test-selector={`newsList_ReadMore_${page.fields["title"]}`}
                                    onClick={() => this.handleButtonOnClick(links[page.nodeId]?.url)}
                                >
                                    {translate("Read More")}
                                </Button>
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>
                ))}
                {totalItems > defaultPageSize && (
                    <GridItem {...styles.paginationGridItem}>
                        <Pagination
                            resultsCount={totalItems}
                            currentPage={page}
                            resultsPerPage={pageSize}
                            resultsPerPageOptions={[
                                defaultPageSize,
                                defaultPageSize * 2,
                                defaultPageSize * 3,
                                defaultPageSize * 4,
                            ]}
                            onChangePage={this.changePage}
                            onChangeResultsPerPage={this.changeResultsPerPage}
                            pageSizeCookie="NewsList-PageSize"
                            {...styles.pagination}
                        />
                    </GridItem>
                )}
            </GridContainer>
        );
    }
}

const widgetModule: WidgetModule = {
    component: withIsInShell(withHistory(connect(mapStateToProps, mapDispatchToProps)(NewsListView))),
    definition: {
        group: "News List",
        allowedContexts: ["NewsListPage"],
        fieldDefinitions: [
            {
                name: fields.defaultPageSize,
                editorTemplate: "IntegerField",
                defaultValue: 5,
                fieldType: "General",
                min: 1,
                max: 20,
            },
        ],
    },
};

export default widgetModule;
